import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  ChevronRight,
  ClipboardList,
  Megaphone,
  Receipt,
  Users,
} from 'lucide-react';
import { useAuth } from '../store/auth';
import { getClaimsStats } from '../api/claims';
import { getAnnouncements, type AnnouncementItem } from '../api/notifications';
import GeoClockCard from '../components/GeoClockCard';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import MetricCard from '../components/ui/MetricCard';
import QuickActionGrid from '../components/ui/QuickActionGrid';
import LoadingState from '../components/ui/LoadingState';
import { formatDate, formatMoney } from '@/lib/format';

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function Dashboard() {
  const { user, onboarding } = useAuth();
  const navigate = useNavigate();
  const showOnboardingBanner = (onboarding?.pendingSteps.length ?? 0) > 0;
  const [claimsStats, setClaimsStats] = useState<{ pendingCount: number; totalAmount: number } | null>(
    null
  );
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [announcementsError, setAnnouncementsError] = useState<string | null>(null);
  const [claimsError, setClaimsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getClaimsStats(), getAnnouncements(1)]).then(([claims, announcementResponse]) => {
      if (claims.data?.data) {
        setClaimsStats({
          pendingCount: claims.data.data.pendingCount,
          totalAmount: claims.data.data.totalAmount,
        });
        setClaimsError(null);
      } else if (claims.error) {
        setClaimsError(claims.error);
      }

      if (announcementResponse.error) {
        setAnnouncements([]);
        setAnnouncementsError(announcementResponse.error);
      } else {
        setAnnouncements(announcementResponse.data?.data.slice(0, 3) ?? []);
        setAnnouncementsError(null);
      }
      setLoading(false);
    });
  }, []);

  const firstName = user?.name?.split(/\s+/)[0] ?? 'there';
  const latestAnnouncement = announcements[0] ?? null;
  const today = formatDate(new Date().toISOString(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  if (loading) {
    return <LoadingState label="Loading dashboard…" rows={4} />;
  }

  return (
    <div className="space-y-6">
      {showOnboardingBanner && (
        <button
          onClick={() => navigate('/onboarding')}
          className="mb-4 w-full rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-left text-sm text-warning"
        >
          <span className="font-medium">Finish setting up your profile</span>
          <span className="block text-xs text-warning/80">
            {onboarding!.pendingSteps.length} step{onboarding!.pendingSteps.length > 1 ? 's' : ''} left — tap to continue.
          </span>
        </button>
      )}
      <div className="px-1">
        <p className="text-xs text-muted-foreground">{today}</p>
        <h2 className="mt-0.5 text-lg font-semibold text-foreground">
          {greeting()}, {firstName}
        </h2>
      </div>

      <Card className="overflow-hidden border-primary/15 bg-gradient-to-br from-primary/8 via-card to-card">
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <Megaphone className="h-4 w-4 text-primary" />
            Announcements
          </CardTitle>
          <Button variant="ghost" size="sm" asChild className="h-8 text-primary">
            <Link to="/announcements">
              View all
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {announcementsError ? (
            <div className="rounded-lg border border-warning/25 bg-warning-soft px-3 py-3 text-sm text-warning">
              <p className="font-medium">Announcements could not be loaded.</p>
              <p className="mt-1 text-xs text-warning/80">{announcementsError}</p>
            </div>
          ) : latestAnnouncement ? (
            <div className="space-y-3">
              <Link to="/announcements" className="block rounded-xl border border-border bg-card/80 p-4 transition-colors hover:bg-card">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <Megaphone className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">
                      Latest update
                    </p>
                    <p className="mt-1 text-sm font-medium text-foreground">{latestAnnouncement.title}</p>
                    <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">
                      {latestAnnouncement.body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
                    </p>
                    <p className="mt-2 text-[10px] text-muted-foreground">
                      {formatDate(latestAnnouncement.sentAt)}
                    </p>
                  </div>
                </div>
              </Link>

              {announcements.length > 1 ? (
                <div className="divide-y divide-border rounded-lg border border-border bg-background/70">
                  {announcements.slice(1, 3).map((announcement) => (
                    <Link
                      key={announcement.id}
                      to="/announcements"
                      className="block px-3 py-3 transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-muted/50"
                    >
                      <p className="text-sm font-medium text-foreground">{announcement.title}</p>
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                        {announcement.body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
                      </p>
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {formatDate(announcement.sentAt)}
                      </p>
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">
              No announcements yet.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base font-medium">Today&apos;s attendance</CardTitle>
          <Button variant="ghost" size="sm" asChild className="h-8 text-primary">
            <Link to="/attendance">
              Details
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          <GeoClockCard compact />
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h3 className="theme-section-label">Claims</h3>
        {claimsError ? (
          <p className="text-sm text-muted-foreground">Could not load claims data.</p>
        ) : claimsStats !== null ? (
          <div className="grid grid-cols-2 gap-3">
            <MetricCard
              icon={Receipt}
              value={claimsStats.pendingCount}
              title="Pending"
              hint="Awaiting approval"
              to="/claims"
            />
            <MetricCard
              icon={Receipt}
              value={formatMoney(claimsStats.totalAmount)}
              title="Total"
              hint="All claims"
              to="/claims"
            />
          </div>
        ) : null}
      </section>

      <section className="space-y-3">
        <h3 className="theme-section-label">Quick actions</h3>
        <QuickActionGrid
          actions={[
            { to: '/leave', icon: CalendarDays, title: 'Leave', description: 'Requests', module: 'leave' },
            { to: '/claims', icon: Receipt, title: 'Claims', description: 'Submit', module: 'claims' },
            {
              to: '/attendance',
              icon: ClipboardList,
              title: 'Attendance',
              description: 'Clock in/out',
              module: 'attendance',
            },
            { to: '/employees', icon: Users, title: 'Employees', description: 'Directory', module: 'team' },
          ]}
          className="grid-cols-2 sm:grid-cols-3"
        />
      </section>
    </div>
  );
}
